import { useEffect } from 'react';
import { useLocation } from 'wouter';

/**
 * useSingleH1Guard - Hook client-side care asigură prezența a maxim un singur tag <h1> pe pagină.
 * 
 * Funcționează prin scanarea DOM-ului după navigare (schimbarea locației).
 * Folosește requestIdleCallback pentru a se asigura că scanarea se realizează doar când 
 * browserul este liber, eliminând orice impact asupra performanței de randare sau a interacțiunii.
 * 
 * Dacă sunt găsite mai multe tag-uri <h1>, prima instanță este păstrată ca <h1>, 
 * iar următoarele sunt convertite automat în <h2> (păstrând atributele, clasele și stilurile inline).
 */
export function useSingleH1Guard() {
  const [location] = useLocation();

  useEffect(() => {
    const enforceSingleH1 = () => {
      const h1s = document.querySelectorAll('h1');
      if (h1s.length <= 1) return;

      // Primul <h1> rămâne neschimbat, restul sunt convertite la <h2>
      for (let i = 1; i < h1s.length; i++) {
        const h1 = h1s[i];
        const h2 = document.createElement('h2');
        
        // Copiază structura HTML din interior
        h2.innerHTML = h1.innerHTML;
        
        // Copiază toate atributele (id, class, style, etc.)
        for (let j = 0; j < h1.attributes.length; j++) {
          const attr = h1.attributes[j];
          h2.setAttribute(attr.name, attr.value);
        }
        
        // Copiază stilul inline exact
        h2.style.cssText = h1.style.cssText;
        
        // Înlocuiește în mod direct elementul în arborele DOM
        h1.parentNode?.replaceChild(h2, h1);
      }
    };

    // Rulează folosind requestIdleCallback pentru impact minim/zero asupra performanței
    if ('requestIdleCallback' in window) {
      const idleId = requestIdleCallback(enforceSingleH1, { timeout: 1000 });
      return () => cancelIdleCallback(idleId);
    } else {
      const timeoutId = setTimeout(enforceSingleH1, 200);
      return () => clearTimeout(timeoutId);
    }
  }, [location]);
}
